import React from 'react';
import { Globe2, MoveRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Separator } from '../Separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { ShareBreakdown } from './ShareBreakdown';
import { OriginEmptyState } from './OriginEmptyState';
import type { BucketRow, OriginCoverage, OriginInference } from '../../types/traffic';
interface OriginInferenceCardProps {
  rows: OriginInference[];
  origins: BucketRow[];
  coverage: OriginCoverage;
}
const confidenceVariant: Record<OriginInference['confidence'], 'secondary' | 'outline'> = {
  high: 'secondary',
  medium: 'secondary',
  low: 'outline'
};
function formatGap(minutes: number | null): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return 'unknown';
  if (minutes === 0) return 'aligned';
  const sign = minutes < 0 ? '−' : '+';
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const rest = absolute % 60;
  return rest === 0 ? `${sign}${hours}h` : `${sign}${hours}h${String(rest).padStart(2, '0')}`;
}

/**
 * Where tunnelled visitors most likely actually are. A VPN rewrites the IP, but not
 * the browser's own time zone or language — so those point back at the real region.
 */
export function OriginInferenceCard({
  rows,
  origins,
  coverage
}: OriginInferenceCardProps) {
  const maskedVisitors = rows.reduce((sum, row) => sum + row.visitors, 0);
  // Clamped for the same reason as the coverage shares: a subset can never exceed
  // the population it is drawn from.
  const maskingRate = coverage.comparable > 0 ? Math.min(1, maskedVisitors / coverage.comparable) : 0;
  return <Card data-id="element-292" data-mp="3fhsoks1no9ud">
      <CardHeader data-id="element-293" data-mp="281wc4ixcvr1k">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-294" data-mp="282e7ldc7218f">
          <CardTitle className="flex items-center gap-2" data-id="element-295" data-mp="240arucihsahc">
            <Globe2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-296" data-mp="23ypygq7wyxl2" />
            Likely true origin
          </CardTitle>
          <Badge variant="outline" data-id="element-297" data-mp="25flsyx5e7f5f">Inference</Badge>
        </div>
        <CardDescription data-id="element-298" data-mp="2ag191wh270r3">
          A VPN changes the IP, not the browser&rsquo;s time zone or language — so those point back
          at the real region
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-299" data-mp="20gsildlek49h">
        {rows.length > 0 ? <>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-300" data-mp="1mqp08xpn8upb">
              <p className="font-mono text-2xl tabular-nums text-foreground" data-id="element-301" data-mp="1rhf5may2kxk0">
                {Math.round(maskingRate * 100)}%
              </p>
              <p className="text-xs text-muted-foreground" data-id="element-302" data-mp="1cu3sw5q5j7ib">
                of {coverage.comparable.toLocaleString()} comparable visitors arrive from an IP
                country that disagrees with their own browser region
              </p>
            </div>

            <h3 className="mt-6 text-sm font-medium text-foreground" data-id="element-303" data-mp="17tg31ndnswpo">Exit vs likely origin</h3>
            <p className="mt-1 text-xs text-muted-foreground" data-id="element-304" data-mp="p548hq33gd7q">
              Every visitor whose IP country disagrees with their inferred region — the disagreement
              itself is the signal, so this does not depend on the VPN heuristic firing
            </p>
            <div className="-mx-1 mt-3 overflow-x-auto px-1" data-id="element-305" data-mp="y3zf33a1vpbk">
              <Table data-id="element-306" data-mp="t5xw0icmjq03">
                <TableHeader data-id="element-307" data-mp="t5o60urkxiue">
                  <TableRow data-id="element-308" data-mp="1849vewrdtcuy">
                    <TableHead data-id="element-309" data-mp="183gitf5qrt4w">IP says</TableHead>
                    <TableHead data-id="element-310" data-mp="1825f1fcaqlds">Network block</TableHead>
                    <TableHead data-id="element-311" data-mp="1343zh4e2x472">Probably from</TableHead>
                    <TableHead data-id="element-312" data-mp="133cawdiwg6qa">Signal</TableHead>
                    <TableHead data-id="element-313" data-mp="1gvikthvglhha">Browser zone</TableHead>
                    <TableHead data-id="element-314" data-mp="1cuykk5fl9xqx">Also flagged</TableHead>
                    <TableHead className="text-right" data-id="element-315" data-mp="1cvi57twmj08n">Clock gap</TableHead>
                    <TableHead className="text-right" data-id="element-316" data-mp="1qtyd3o682wsd">Visitors</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-317" data-mp="1lx7sqondxf15">
                  {rows.map(row => <TableRow key={`${row.exitCountry}-${row.likelyOrigin}-${row.browserTimeZone}`} data-id="element-318" data-mp="25yki9r2i1ob3">
                      <TableCell className="whitespace-nowrap font-mono text-xs" data-id="element-319" data-mp="29x9tpe3z8vs3">
                        {row.exitCity}, {row.exitCountry}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground" data-id="element-320" data-mp="2jnuu9737oadn">
                        {row.networkBlocks.length > 0 ? row.networkBlocks.join(', ') : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap" data-id="element-321" data-mp="1r0s87efq1s4a">
                        <span className="flex items-center gap-1.5 font-mono text-xs text-foreground" data-id="element-322" data-mp="1vzbrt86uerth">
                          <MoveRight className="h-3 w-3 text-muted-foreground" aria-hidden="true" data-id="element-323" data-mp="20zpra1udotfw" />
                          {row.likelyOrigin}
                        </span>
                      </TableCell>
                      <TableCell data-id="element-324" data-mp="10qrhnxgzkyqa">
                        <Badge variant={confidenceVariant[row.confidence]} data-id="element-325" data-mp="10pq49mci005e">
                          {row.confidence} · {row.basis}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs" data-id="element-326" data-mp="qz3rdzw61nqe">
                        {row.browserTimeZone}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-id="element-327" data-mp="i0a1sth95xmc">
                        {row.vpnFlagged ? 'VPN heuristic' : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right font-mono text-xs tabular-nums" data-id="element-328" data-mp="81u76gj0o683">
                        {formatGap(row.clockGapMinutes)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-329" data-mp="22fv5n0anmr7n">
                        {row.visitors.toLocaleString()}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </> : <OriginEmptyState coverage={coverage} data-id="element-330" data-mp="itgivtbe725" />}

        {origins.length > 0 && <>
            <Separator className="my-6" data-id="element-331" data-mp="3hzkqx04de9o2" />
            <h3 className="text-sm font-medium text-foreground" data-id="element-332" data-mp="3hwq4j31sqes0">
              All visitors by inferred region
            </h3>
            <p className="mt-1 text-xs text-muted-foreground" data-id="element-333" data-mp="ei2rnh2nyaz9">
              Independent of IP, so this holds even when geo-IP coverage is thin — compare it with
              the Countries card to spot systematic masking
            </p>
            <div className="mt-3" data-id="element-334" data-mp="5gcudum3ohtk">
              <ShareBreakdown rows={origins.map(origin => ({
            label: origin.label,
            share: origin.share,
            detail: (origin.visitors ?? origin.requests).toLocaleString()
          }))} data-id="element-335" data-mp="5jh231yrxvoz" />
            </div>
          </>}

        <p className="mt-6 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-336" data-mp="2u6vvkwkllad1">
          Network blocks are IPs truncated to their /24 (or /48 for IPv6) in the browser — enough to
          recognise a VPN exit node or corporate egress, never enough to identify a person. Full IP
          addresses are deliberately not collected or stored.
        </p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-id="element-337" data-mp="2rpk7s47ysik3">
          Regional inference, not identification. It is wrong for travellers, for anyone whose
          machine is set to another region, and for corporate laptops imaged elsewhere — and it never
          names an individual. Treat it as a population-level signal only.
        </p>
      </CardContent>
    </Card>;
}